import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../components/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { demoWorkflow, sampleRecords, faqData } from '../data/mockData';

export default function DemoView() {
  const [activeStep, setActiveStep] = useState('1');
  const [opState, setOpState] = useState('success');

  const renderSimulatedState = () => {
    const states = {
      success: { icon: FiIcons.FiCheckCircle, color: 'text-green-400', bg: 'bg-green-400/10', border: 'border-green-400/30', label: 'SYSTEM NOMINAL', desc: 'All telemetry and data points synchronized successfully.' },
      warning: { icon: FiIcons.FiAlertCircle, color: 'text-coral-400', bg: 'bg-coral-400/10', border: 'border-coral-400/30', label: 'BUDGET ALERT', desc: 'Projected costs exceed baseline by 12.4%.' },
      error: { icon: FiIcons.FiXCircle, color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/30', label: 'SYNC INTERRUPTED', desc: 'Encryption handshake failed in tunnel 4.' },
      empty: { icon: FiIcons.FiInbox, color: 'text-gray-500', bg: 'bg-white/5', border: 'border-white/10', label: 'NO DATA', desc: 'Initialize the trip builder to begin generating lanes.' },
      progress: { icon: FiIcons.FiLoader, color: 'text-cyan-400', bg: 'bg-cyan-400/10', border: 'border-cyan-400/30', label: 'SYNTHESIZING', desc: 'Recalculating routing logic based on latest patterns.' }
    };
    const s = states[opState];
    return (
      <motion.div key={opState} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={`p-6 rounded-2xl border ${s.bg} ${s.border} flex flex-col items-center text-center`}>
        <SafeIcon icon={s.icon} className={`text-4xl ${s.color} mb-4 ${opState === 'progress' ? 'animate-spin' : ''}`} />
        <h4 className={`text-sm font-mono font-bold tracking-widest ${s.color}`}>{s.label}</h4>
        <p className="text-xs text-gray-400 mt-2 leading-relaxed">{s.desc}</p>
      </motion.div>
    );
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-6xl mx-auto space-y-16 pb-20">
      {/* 1. Header & How it works */}
      <section className="border-b border-white/5 pb-12">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-8">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-2 px-2 py-1 rounded bg-cyan-400/10 text-cyan-400 text-[10px] font-mono border border-cyan-400/20">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse"></span>
              LIVE PRODUCT DEMO
            </div>
            <h2 className="text-4xl font-bold text-white tracking-tighter">DEMO / HOW IT WORKS</h2>
            <p className="text-gray-400 max-w-xl text-sm leading-relaxed">
              Experience the RouteMuse travel ecosystem. This interactive environment demonstrates the core traveler journey from initial synthesis to offline mission execution.
            </p>
          </div>
          <div className="text-center font-mono text-[10px] text-gray-600 bg-white/5 p-4 rounded-xl border border-white/5">
            MADE BY LINGLINK AI AND DIGLAB AI
          </div>
        </div>

        {/* 2. Try This Flow */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-4">
            <h3 className="text-xs font-mono text-gray-500 tracking-[0.3em] uppercase mb-4">Try This Flow</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {demoWorkflow.map((step) => (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(step.id)}
                  className={`flex items-start gap-4 p-4 rounded-2xl border text-left transition-all ${
                    activeStep === step.id ? 'bg-cyan-400/10 border-cyan-400/40 shadow-[0_0_20px_rgba(0,240,255,0.05)]' : 'bg-white/5 border-white/5 hover:border-white/20'
                  }`}
                >
                  <div className={`p-2 rounded-lg ${activeStep === step.id ? 'bg-cyan-400 text-midnight-900' : 'bg-white/10 text-gray-500'}`}>
                    <SafeIcon icon={FiIcons[step.icon]} />
                  </div>
                  <div>
                    <h4 className={`text-sm font-bold ${activeStep === step.id ? 'text-white' : 'text-gray-400'}`}>{step.title}</h4>
                    <p className="text-[10px] text-gray-500 mt-1 leading-tight">{step.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* 3. Simulated States */}
          <div className="space-y-6">
            <h3 className="text-xs font-mono text-gray-500 tracking-[0.3em] uppercase mb-4">Simulated States</h3>
            <div className="flex flex-wrap gap-2 mb-4">
              {['success', 'warning', 'error', 'empty', 'progress'].map(state => (
                <button key={state} onClick={() => setOpState(state)} className={`px-2 py-1 rounded text-[9px] font-mono border ${opState === state ? 'bg-cyan-400 text-midnight-900 border-cyan-400' : 'text-gray-500 border-white/10'}`}>
                  {state.toUpperCase()}
                </button>
              ))}
            </div>
            {renderSimulatedState()}
          </div>
        </div>
      </section>

      {/* 4. Sample Data Records */}
      <section className="glass-card p-8 border-dashed border-white/10 overflow-hidden">
        <h3 className="text-xs font-mono text-gray-500 tracking-[0.3em] uppercase mb-6">Sample Data Records</h3>
        <div className="overflow-x-auto -mx-8 px-8">
          <table className="w-full text-left font-mono text-[11px] min-w-[600px]">
            <thead>
              <tr className="text-gray-600 border-b border-white/5 uppercase tracking-tighter">
                <th className="pb-4">Record ID</th>
                <th className="pb-4">Category</th>
                <th className="pb-4">Entity</th>
                <th className="pb-4">Status</th>
                <th className="pb-4 text-right">Value</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              {sampleRecords.map(row => (
                <tr key={row.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="py-4 text-cyan-400">{row.id}</td>
                  <td className="py-4 text-gray-500">{row.type}</td>
                  <td className="py-4 font-sans font-medium text-white">{row.name}</td>
                  <td className="py-4">
                    <span className={`px-2 py-0.5 rounded-full text-[9px] border ${row.status === 'Confirmed' || row.status === 'Success' ? 'border-green-400/30 text-green-400' : 'border-coral-400/30 text-coral-400'}`}>
                      {row.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="py-4 text-right text-white opacity-80">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* 5. FAQ Section */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-12">
        <div>
          <h3 className="text-2xl font-bold text-white mb-4">Common Protocols</h3>
          <p className="text-gray-400 text-sm mb-8 leading-relaxed">
            Everything you need to know about the RouteMuse architectural framework and data privacy standards.
          </p>
          <div className="p-6 rounded-2xl bg-coral-400/5 border border-coral-400/20">
            <h4 className="text-sm font-bold text-coral-400 mb-2">Need Enterprise Support?</h4>
            <p className="text-xs text-gray-500 mb-4">Custom white-label solutions for travel agencies and corporate retreats.</p>
            <button className="text-xs font-mono text-white border-b border-coral-400 pb-1">CONTACT OPERATIONAL HQ</button>
          </div>
        </div>
        <div className="space-y-4">
          {faqData.map((item, idx) => (
            <div key={idx} className="p-5 rounded-2xl bg-white/5 border border-white/5 hover:border-white/20 transition-all cursor-default">
              <h4 className="text-sm font-bold text-white mb-2">{item.q}</h4>
              <p className="text-xs text-gray-500 leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </section>
    </motion.div>
  );
}